/* eslint-disable @typescript-eslint/no-explicit-any */
import { createFeed } from '../elements/feed/feeds.utils';
import { serializeWidgetTextValue } from './versions/widgetText-serializer';
import { isUrl } from '@studio/utils/url';
import { parseColor } from '../color.utils';
import { deserializeVersionedText, serializeVersionedText } from './text-serializer';
/** Deserialize property values that are serialized as strings, i.e in BrandLibrary */
export function deserializeElementPropertyStringValue(key, value) {
    var _a;
    if (value === null || value === undefined) {
        return undefined;
    }
    switch (key) {
        case 'duration':
        case 'time':
        case 'lineHeight':
        case 'characterSpacing':
        case 'fontSize':
        case 'originX':
        case 'originY':
        case 'scaleX':
        case 'scaleY':
        case 'opacity':
        case 'rotationX':
        case 'rotationY':
        case 'rotationZ':
        case 'ratio':
            return parseFloat(value);
        case 'x':
        case 'y':
        case 'width':
        case 'height':
        case 'maxRows':
            return Number(value);
        case 'horizontalAlignment':
        case 'verticalAlignment':
        case 'textOverflow':
            return value;
        case 'hidden':
        case 'locked':
        case 'mirrorX':
        case 'mirrorY':
        case 'strikethrough':
        case 'underline':
        case 'uppercase':
            return (typeof value === 'string' ? value.toLowerCase() === 'true' : value);
        case 'font': {
            if (value === undefined) {
                return undefined;
            }
            const fontStyle = (typeof value === 'string' ? value.split(' ') : []);
            let src = fontStyle[3];
            try {
                // Support old font url that where encoded with base64 without data prefix.
                //
                // Note: that exported HTML creatives can have base64 encoded fonts which begins with data prefix.
                if (!src.startsWith('data') && !isUrl(src)) {
                    if (typeof window !== 'undefined') {
                        src = atob(src);
                    }
                    else {
                        src = Buffer.from(src, 'base64').toString();
                    }
                }
            }
            catch (_b) {
                /* empty */
            }
            return {
                id: fontStyle[0],
                weight: parseInt(fontStyle[1], 10),
                style: fontStyle[2],
                src,
                fontFamilyId: fontStyle[4]
            };
        }
        case 'imageAsset': {
            try {
                value = JSON.parse(value);
                if (typeof value === 'object') {
                    return value;
                }
            }
            catch (_c) {
                const imageAsset = value.split(' ');
                // old images are missing gen ai flag. We have to detect that in order
                // to know if name is on 4th or 5th position
                const hasGenAiFlag = (_a = imageAsset[4]) === null || _a === void 0 ? void 0 : _a.match(/^(true|false|undefined)$/);
                return {
                    id: imageAsset[0],
                    url: imageAsset[1],
                    width: parseInt(imageAsset[2], 10),
                    height: parseInt(imageAsset[3], 10),
                    isGenAi: !!hasGenAiFlag && imageAsset[4] === 'true',
                    name: hasGenAiFlag ? imageAsset[5] : imageAsset[4]
                };
            }
            break;
        }
        case 'padding': {
            const padding = value.split(' ') || [];
            return {
                top: padding[0] ? parseFloat(padding[0]) : 0,
                right: padding[1] ? parseFloat(padding[1]) : 0,
                bottom: padding[2] ? parseFloat(padding[2]) : 0,
                left: padding[3] ? parseFloat(padding[3]) : 0
            };
        }
        case 'border': {
            const border = value.split(' ');
            if (border.length <= 0) {
                return undefined;
            }
            return {
                thickness: parseInt(border[0], 10),
                style: border[1],
                color: parseColor(border[2])
            };
        }
        case 'shadows': {
            const shadows = value ? splitSerializedShadowValue(value) : undefined;
            if (!shadows) {
                return undefined;
            }
            return shadows.map((s) => {
                const shadow = s.split(' ');
                return {
                    offsetX: parseInt(shadow[0], 10),
                    offsetY: parseInt(shadow[1], 10),
                    blur: parseInt(shadow[2], 10),
                    spread: parseInt(shadow[3], 10),
                    color: parseColor(shadow[4])
                };
            });
        }
        case 'textShadows': {
            const textShadows = value
                ? splitSerializedShadowValue(value)
                : undefined;
            if (!textShadows) {
                return undefined;
            }
            return textShadows.map((s) => {
                const shadow = s.split(' ');
                return {
                    offsetX: parseInt(shadow[0], 10),
                    offsetY: parseInt(shadow[1], 10),
                    blur: parseInt(shadow[2], 10),
                    color: parseColor(shadow[3])
                };
            });
        }
        case 'textColor':
        case 'fill':
            return parseColor(value);
        case 'feed':
            return deserializeFeedString(value);
        case 'filters':
        case 'imageSettings':
        case 'states':
        case 'animations':
        case 'videoAsset':
        case 'videoSettings':
        case 'actions':
        case 'radius':
            if (typeof value === 'string') {
                return value.length ? JSON.parse(value) : value;
            }
            return value;
        case 'content': {
            return (typeof value === 'string'
                ? deserializeVersionedText(value)
                : value);
        }
        default:
            return value;
    }
}
export function deserializeTextStyle(key, value) {
    switch (key) {
        case '__fontFamilyId':
            return value;
        case 'variable':
            return deserializeElementPropertyStringValue('feed', value);
        default:
            return deserializeElementPropertyStringValue(key, value);
    }
}
export function serializeTextStyle(key, value) {
    switch (key) {
        case '__fontFamilyId':
            return value;
        case 'variable':
            return serializeFeedValue(value);
        case 'textColor':
            return serializeElementPropertyToStringValue('textColor', value);
        case 'font':
            return serializeElementPropertyToStringValue('font', value);
        case 'textShadows':
            return serializeElementPropertyToStringValue('textShadows', value);
        default:
            return value === null || value === void 0 ? void 0 : value.toString();
    }
}
/** Poorly typed, should only be used by version property due to messy types */
export function deserializeVersionPropertyValue(propertyName, value) {
    if (value === null) {
        value = undefined;
    }
    switch (propertyName) {
        case 'feed':
            return deserializeFeedString(value);
        case 'content': {
            // Elements doesn't have "content" and value will instead be fetched from Version.properties
            if (value === undefined) {
                return undefined;
            }
            return typeof value === 'string' ? deserializeVersionedText(value) : value;
        }
        case 'text':
        case 'widgetText': {
            if (typeof value === 'string') {
                return value.length ? JSON.parse(value) : value;
            }
            return value;
        }
        default:
            return value;
    }
}
export function serializeVersionPropertyValue(propertyName, value) {
    switch (propertyName) {
        case 'feed':
            return serializeFeedValue(value);
        case 'content': {
            return typeof value === 'string' ? value : serializeVersionedText(value);
        }
        case 'widgetText': {
            return typeof value === 'string' ? value : serializeWidgetTextValue(value);
        }
        default:
            return typeof value === 'string' ? value : JSON.stringify(value);
    }
}
/**
 * Serialize propertyValue to a string
 * @param propertyName
 * @param value
 */
export function serializeElementPropertyToStringValue(key, value) {
    switch (key) {
        case 'duration':
        case 'time':
        case 'lineHeight':
        case 'characterSpacing':
        case 'fontSize':
        case 'originX':
        case 'originY':
        case 'scaleX':
        case 'scaleY':
        case 'opacity':
        case 'rotationX':
        case 'rotationY':
        case 'rotationZ':
        case 'ratio':
        case 'x':
        case 'y':
        case 'width':
        case 'height':
        case 'maxRows':
            return `${value}`;
        case 'horizontalAlignment':
        case 'verticalAlignment':
        case 'textOverflow':
            return value;
        case 'hidden':
        case 'locked':
        case 'mirrorX':
        case 'mirrorY':
        case 'strikethrough':
        case 'underline':
        case 'uppercase':
            return Boolean(value).toString();
        case 'font': {
            const fontStyle = value;
            const src = fontStyle.src;
            return `${fontStyle.id} ${fontStyle.weight} ${fontStyle.style} ${src} ${fontStyle.fontFamilyId}`;
        }
        case 'border': {
            const border = value;
            return border ? `${border.thickness} ${border.style} ${border.color}` : '';
        }
        case 'padding': {
            const padding = value;
            return `${padding.top} ${padding.right || ''} ${padding.bottom || ''} ${padding.left || ''}`;
        }
        case 'shadows': {
            const shadows = value;
            return shadows
                ? shadows
                    .map(s => `${s.offsetX} ${s.offsetY} ${s.blur} ${s.spread} ${s.color}`)
                    .join(',')
                : '';
        }
        case 'textShadows': {
            const textShadow = value;
            return textShadow
                ? textShadow.map(s => `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.color}`).join(',')
                : '';
        }
        case 'textColor':
        case 'fill': {
            const color = value;
            if (!color) {
                return '';
            }
            else {
                return color.toString();
            }
        }
        case 'content':
            return serializeVersionedText(value);
        case 'feed':
            return serializeFeedValue(value);
        case 'filters':
        case 'imageSettings':
        case 'imageAsset':
        case 'states':
        case 'animations':
        case 'videoAsset':
        case 'videoSettings':
        case 'actions':
        case 'radius':
            return JSON.stringify(value);
        default:
            return value;
    }
}
export function splitSerializedShadowValue(str) {
    return str.split(/,(?![^(]*\))/);
}
export function deserializeVersionProperty(versionProperty) {
    let newValue = deserializeVersionPropertyValue(versionProperty.name, versionProperty.value);
    if (!newValue) {
        throw new Error(`Couldn't deserialize version property`);
    }
    if (versionProperty.name === 'widgetText') {
        newValue = {
            text: newValue.text
        };
    }
    return {
        id: versionProperty.id,
        name: versionProperty.name,
        value: newValue
    };
}
export function deserializeVersionProperties(versionProperties = []) {
    return versionProperties.map(versionProperty => deserializeVersionProperty(versionProperty));
}
export function deserializeFeedString(value) {
    var _a, _b, _c, _d, _e;
    if (!!value && typeof value !== 'string' && typeof value === 'object' && 'path' in value) {
        return value;
    }
    if (typeof value === 'string') {
        try {
            const feed = JSON.parse(value);
            if (!('path' in feed)) {
                // Continue to old deserialization
                throw new Error();
            }
            return feed;
        }
        catch (_f) {
            const feedValues = value.split(' ');
            const feed = {
                id: feedValues[0],
                path: (_a = feedValues[1]) !== null && _a !== void 0 ? _a : '',
                step: {
                    start: parseInt((_b = feedValues[2]) !== null && _b !== void 0 ? _b : 1, 10),
                    size: parseInt((_c = feedValues[3]) !== null && _c !== void 0 ? _c : 1, 10),
                    occurrence: ((_d = feedValues[4]) !== null && _d !== void 0 ? _d : 'loop')
                },
                fallback: (_e = feedValues[5]) !== null && _e !== void 0 ? _e : '',
                type: 'text'
            };
            return feed;
        }
    }
    return createFeed('');
}
export function serializeFeedValue(feed) {
    if (typeof feed === 'object') {
        return JSON.stringify(feed);
    }
    if (typeof feed === 'string') {
        return feed;
    }
    return JSON.stringify(createFeed(''));
}
